import {Select} from '@smart/react-components';
import type {SelectItemProps} from '@smart/react-components';
import classNames from 'classnames';
import type {CSSProperties} from 'react';
import React, {useContext, useEffect} from 'react';
import type {StringSchema} from 'yup';
import type {Maybe} from '../../../graphql';
import {FieldContext} from './field-wrapper';
import {parseFieldErrors} from './helpers';
import styles from './select-field.module.scss';
import type {ICommonFieldProps, IWrapperProvider} from './types';
import {useFieldSetup} from './use-field-setup';

type SelectInputProps = React.ComponentProps<typeof Select>;

interface IProps extends ICommonFieldProps {
  options: SelectItemProps[];
  initial?: string;
  className?: string;
  lightTheme?: boolean;
  style?: CSSProperties;
  props?: Partial<SelectInputProps>;
  schema?: StringSchema<any>;
  label?: Maybe<string>;
  allowEmptySelection?: boolean;
  panelClassName?: string;
}

const SelectField = function ({
  tag,
  initial = ``,
  options,
  lightTheme,
  schema,
  required,
  props,
  label,
  style,
  allowEmptySelection = false,
  panelClassName,
}: IProps) {
  const {getLabel, genericHandler, disabled, state, errors, showErrors} =
    useContext<IWrapperProvider>(FieldContext as any);

  const {className, disabled: disabledInProps} = props || {};

  useFieldSetup(tag, initial, schema);

  const [isInvalid, errorMessage] = parseFieldErrors(tag, errors, showErrors);

  useEffect(() => {
    if (
      options.filter((opt) => opt.value === tag).length === 0 &&
      !(allowEmptySelection && initial === ``)
    ) {
      genericHandler(
        tag,
        options[0]?.value ||
          (options.some((e) => e.value === initial) && initial) ||
          ``,
      );
    }
  }, [options, initial]);

  return tag in state ? (
    <Select
      name={tag as string}
      label={getLabel(tag, required, label)}
      items={options}
      selectType="single"
      value={state[tag] as string}
      onSelectionChange={(value) => genericHandler(tag, value)}
      invalid={isInvalid}
      error={errorMessage}
      disabled={disabled || disabledInProps}
      className={className}
      style={style}
    >
      {(items) => (
        <div
          className={classNames(
            styles[`select-panel`],
            {
              [styles[`select-panel-light`]!]: !!lightTheme,
            },
            panelClassName,
          )}
        >
          {items.map((item) => (
            <Select.Item key={item.value} {...item} />
          ))}
        </div>
      )}
    </Select>
  ) : null;
};

export default SelectField;
